import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';

import { FolderId, FolderIdData, FolderNameData } from 'modules/domains/foldering';
import type { AppDispatch } from 'modules/app/components/App';
import { deleteJourneyContentsAndFoldersMutation } from 'api/mutations/workflowIndex';

import { NamedFolder, ResponseWrapper } from 'ui/toast';

import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';

import {
  GraphQLArchiveWorkflowsAndDeleteFoldersError,
  GraphQLArchiveWorkflowsAndDeleteFoldersKnownFailures,
  GraphQLArchiveWorkflowsAndDeleteFoldersMutationResponseData,
  GraphQLArchiveWorkflowSuccess,
  GraphQLDeleteFolderSuccess,
  GraphQLDeleteJourneyToFolderMutationVariables,
  WorkflowIdData,
  WorkflowNameData,
  WorkflowTypeData,
  WorkflowId,
} from '../types';

const displayText = {
  genericError: 'Could not delete',
  defaultFolderName: 'Unspecified',
};

const keyItemsById = <ItemType extends { id: number }>(items: ItemType[]) =>
  items.reduce<Record<ItemType['id'], ItemType>>(
    (previousValue, item) => ({ ...previousValue, [item.id]: item }),
    {} as Record<ItemType['id'], ItemType>,
  );

const showKnownError = (
  failure: GraphQLArchiveWorkflowsAndDeleteFoldersError,
  foldersById: Record<FolderId, FolderNameData>,
  dispatch: AppDispatch,
) => {
  switch (failure.__typename) {
    case GraphQLArchiveWorkflowsAndDeleteFoldersKnownFailures.CannotDeleteRootFolderFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>Cannot delete root folder.</ResponseWrapper>,
          'CannotDeleteRootFolderFailure',
        ),
      );
      break;
    }

    case GraphQLArchiveWorkflowsAndDeleteFoldersKnownFailures.FolderDoesNotExistFailure: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>
            <NamedFolder name={foldersById[failure.id]?.name || ''} /> folder can not be deleted. It
            does not exist.
          </ResponseWrapper>,
          'FolderDoesNotExistFailure',
        ),
      );
      break;
    }

    default: {
      dispatch(
        addNegativeToastWithErrorId(
          <ResponseWrapper>
            <NamedFolder name={foldersById[failure.id]?.name || displayText.defaultFolderName} />{' '}
            folder could not be deleted.
          </ResponseWrapper>,
          'UnknownFolderDeletionFailure',
        ),
      );
    }
  }
};

const showSuccess = (
  success: GraphQLDeleteFolderSuccess | GraphQLArchiveWorkflowSuccess,
  foldersById: Record<FolderId, FolderIdData & FolderNameData>,
  deletedById: Record<WorkflowId, WorkflowIdData & WorkflowNameData & WorkflowTypeData>,
  dispatch: AppDispatch,
) => {
  const journeyOrFolderName = success?.name || '';
  if (foldersById[success?.id]?.name === journeyOrFolderName) {
    return dispatch(
      addPositiveToast(
        <ResponseWrapper>
          <NamedFolder name={journeyOrFolderName} /> was successfully deleted.
        </ResponseWrapper>,
      ),
    );
  }

  if (deletedById[success?.id]?.name === journeyOrFolderName) {
    return dispatch(
      addPositiveToast(
        <ResponseWrapper>
          Journey <strong>{journeyOrFolderName}</strong> was successfully deleted.
        </ResponseWrapper>,
      ),
    );
  }

  dispatch(
    addPositiveToast(
      <ResponseWrapper>
        Journey <strong>{journeyOrFolderName}</strong> was successfully archived.
      </ResponseWrapper>,
    ),
  );
};

export function useArchiveWorkflowsAndDeleteFolders() {
  const [archiveWorkflowsAndDeleteFolders] = useMutation<
    GraphQLArchiveWorkflowsAndDeleteFoldersMutationResponseData,
    GraphQLDeleteJourneyToFolderMutationVariables
  >(deleteJourneyContentsAndFoldersMutation);

  const dispatch = useDispatch();

  return useCallback(
    async (
      folders: (FolderIdData & FolderNameData)[],
      workflows: (WorkflowIdData & WorkflowNameData & WorkflowTypeData)[],
      deletedWorkflows: (WorkflowIdData & WorkflowNameData & WorkflowTypeData)[] = [],
    ) => {
      const mutationVariables = {
        folderIds: folders.map(({ id }) => id),
        content: [...workflows, ...deletedWorkflows].map(({ id, workflowType }) => ({
          id,
          type: workflowType,
        })),
      };

      try {
        const { data, errors } = await archiveWorkflowsAndDeleteFolders({
          variables: mutationVariables,
        });

        if (!data?.deleteContentsAndFolders || errors) {
          dispatch(
            addNegativeToastWithErrorId(
              displayText.genericError,
              'useArchiveWorkflowsAndDeleteFolders',
            ),
          );
          return;
        }

        const { folderFailures, deletedContent } = data.deleteContentsAndFolders;
        const foldersById = keyItemsById(folders);
        const deletedById = keyItemsById(deletedWorkflows);
        folderFailures.forEach((failure) => showKnownError(failure, foldersById, dispatch));
        deletedContent.forEach((success) =>
          showSuccess(success, foldersById, deletedById, dispatch),
        );
      } catch (error) {
        dispatch(
          addNegativeToastWithErrorId(
            displayText.genericError,
            'useArchiveWorkflowsAndDeleteFolders',
            error,
          ),
        );
      }
    },
    [archiveWorkflowsAndDeleteFolders],
  );
}
